import { lazy, useEffect } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import Spinner from 'components/Base/Spinner';
import { useGetEventsByIdQuery } from 'redux/apiSlice/events.slice';
import { TRtkErrorType } from 'data/types/generic.types';
import ExpiredEventLinkView from 'pages/public/Event/ExpiredEventLinkView';
import { RegisterAction } from 'components/Shared/RegisterAction';

import { NavBarRouteType, RoutesPaths, RouteType } from './Routes.types';

const EventById = lazy(() => import('pages/public/Event'));
const PageNotFound = lazy(() => import('pages/public/PageNotFound'));
const ShippingKitInfo = lazy(() => import('pages/public/ShippingKitInfo'));
const MeetingLink = lazy(() => import('pages/public/MeetingLink'));

export const LandingResolver = () => {
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action');
  const eventId = searchParams.get('id') || '';

  const shouldShowEvent = action === 'showEventEvent' && !!eventId;

  const { data, isLoading, error } = useGetEventsByIdQuery(eventId, {
    skip: !shouldShowEvent,
  });

  // Auto-open registration when event data is ready
  useEffect(() => {
    let timer: number | null = null;
    if (data) {
      timer = window.setTimeout(() => {
        const registerBtn = document.querySelector(
          '[data-cy="register-btn"]',
        ) as HTMLButtonElement;
        if (registerBtn) registerBtn.click();
      }, 100);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [data]);

  // No navigation; keep URL. Just resolve and render appropriate UI.

  if (!shouldShowEvent) {
    return <Navigate to={RoutesPaths.PAGE_NOT_FOUND} replace />;
  }

  if (isLoading) {
    return (
      <div
        className="w-100"
        style={{ display: 'flex', justifyContent: 'center', padding: 40 }}
      >
        <Spinner spinnerSize={40} />
      </div>
    );
  }

  if ((error as TRtkErrorType)?.status === 404) {
    return <Navigate to={RoutesPaths.PAGE_NOT_FOUND} replace />;
  }
  if ((error as TRtkErrorType)?.status === 400) {
    return <ExpiredEventLinkView />;
  }

  if (!data) return null;

  // Successfully fetched event, show centered Register button (same UI as before)
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '20px',
      }}
    >
      <div style={{ maxWidth: '300px' }}>
        <RegisterAction
          eventId={eventId}
          isAllClassesFinished={false}
          size="large"
        />
      </div>
    </div>
  );
};

export const appRoutes: RouteType[] = [
  {
    component: EventById,
    path: RoutesPaths.EVENTS_BY_ID,
    title: 'Event',
  },
  {
    component: PageNotFound,
    path: RoutesPaths.PAGE_NOT_FOUND,
    title: '404',
  },
  {
    component: ShippingKitInfo,
    path: RoutesPaths.KIT_SHIPPING_BY_ID,
    title: 'Shipping kits',
  },
];

export const navbarRoutes: NavBarRouteType[] = [
  {
    path: RoutesPaths.HOME,
    title: 'Home',
  },
  {
    path: RoutesPaths.ABOUT,
    title: 'About & FAQs',
  },
  {
    path: RoutesPaths.FAQS_SPANISH,
    title: 'Acerca de y preguntas frecuentes',
  },
  {
    path: RoutesPaths.FOR_FAMILIES,
    title: 'For Families',
  },
  {
    path: RoutesPaths.FOR_FAMILIES_SPANISH,
    title: 'Para Familias',
  },
  {
    path: RoutesPaths.CHILD_CARE,
    title: 'Child Care & Preschool Providers',
  },
];

export const footerRoutes: NavBarRouteType[] = [
  {
    path: RoutesPaths.IDAHO_ABOUT,
    title: 'ABOUT IDAEYC',
  },
  {
    path: RoutesPaths.IDAHO_FAMILY_RESOURCES,
    title: 'FAMILY RESOURCES',
  },
  {
    path: RoutesPaths.IDAHO_JOB_BOARD,
    title: 'JOB BOARD',
  },
  {
    path: RoutesPaths.IDAHO_ECE_RESOURCES,
    title: 'ECE RESOURCES',
  },
  {
    path: RoutesPaths.IDAHO_GET_INVOLVED,
    title: 'GET INVOLVED',
  },
];

export const outsideAppLayoutRoutes: RouteType[] = [
  {
    component: MeetingLink,
    path: RoutesPaths.MEETING_LINK,
    title: 'Meeting link',
  },
];
